import { getDepositTitle } from "@/modules/deposit/helpers";
import { useGetDepositByIdQuery } from "@/modules/deposit/services";
import { AppPaths, CloudflareAsset, SideSheetPosition } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { DetailActions, EmptyOrErrorContent } from "@/shared/components";
import { buildRoute, getErrorMessage } from "@/shared/helpers";
import { useAppSelector, useMediaQuery } from "@/shared/hooks";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { DepositDetailHistory } from "./DepositDetailHistory";
import { DepositDetailRows } from "./DepositDetailRows";

interface Props {
  depositId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const DepositDetails = ({
  depositId = "",
  isOpen,
  onClose,
}: Props) => {
  const navigate = useNavigate();
  const featureToggle = useAppSelector(state => state.featureToggle.toggles);
  const isDesktop = useMediaQuery();
  const { t } = useTranslation();

  const {
    data: deposit,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetDepositByIdQuery({ id: depositId });

  const navigateToStatementPage = () => {
    const url = buildRoute(AppPaths.DepositStatement, { depositId });
    navigate(url);
  };

  const navigateToSchedulePage = () => {
    const url = buildRoute(AppPaths.DepositSchedule, { depositId });
    navigate(url);
  };

  const actions = [
    {
      id: 0,
      title: t("general.statement.title"),
      icon: CloudflareAsset.DocumentDeclaration,
      isAvailable: featureToggle.isDepositStatementAvailable,
      onClick: () => navigateToStatementPage(),
    },
    {
      id: 1,
      title: t("general.schedule.title"),
      icon: CloudflareAsset.DocumentList,
      isAvailable: featureToggle.isDepositScheduleAvailable,
      onClick: () => navigateToSchedulePage(),
    }
  ].filter((action) => action.isAvailable);

  return (
    <SideSheet
      position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
      isPositioned={!isDesktop}
      withOverlay={!isDesktop}
      isOpen={isOpen}
      isBodyHidden={false}
      onClose={onClose}
      isWithDivider
      isLoading={isFetching}
      className="detail-side-sheet"
      size="small"
      title={
        deposit?.name || t(getDepositTitle(
          !!deposit?.isReplenishable,
          !!deposit?.isWithdrawable
        ))
      }
      shouldRemoveCallback={false}
      closeOnOutsideClick={false}
    >
      {({ scrollbarContainerRef }) => (
        <>
          {isFetching ? (
            <Spinner isFullScreen={false} size="large"/>
          ) : isError && !isFetching ? (
            <div className="full-width full-height flexbox align-items--center">
              <EmptyOrErrorContent
                isError={true}
                title={t(getErrorMessage(error as IError))}
                action={refetch}
              />
            </div>
          ) : (
            <>
              <DepositDetailRows
                deposit={deposit}
                scrollbarContainerRef={scrollbarContainerRef}
              />
              <DetailActions actions={actions} page="Deposit detail"/>
              {depositId && (
                <DepositDetailHistory depositId={depositId}/>
              )}
            </>
          )}
        </>
      )}
    </SideSheet>
  );
};
